import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, Download, Plus, Trash2, CheckCircle, TrendingUp, TrendingDown, DollarSign, Star, Send, Copy, Loader2, ExternalLink, Video } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import BreakdownProposalGenerator from '../components/BreakdownProposalGenerator';
import UploadPdfProposalCard from '../components/UploadPdfProposalCard';

const BreakDownReport = () => {
  const [clientName, setClientName] = useState('');
  const [brokerName, setBrokerName] = useState('');
  const [loanPurpose, setLoanPurpose] = useState('purchase'); // 'purchase' or 'refinance'
  
  // Refinance specific fields
  const [currentLoanBalance, setCurrentLoanBalance] = useState('');
  const [newLoanBalance, setNewLoanBalance] = useState('');
  
  // Lender Options
  const [lender1Name, setLender1Name] = useState('');
  const [lender1Rate, setLender1Rate] = useState('');
  const [lender1Type, setLender1Type] = useState('Variable');
  const [lender1Repayment, setLender1Repayment] = useState('Principal & Interest');
  const [lender1Features, setLender1Features] = useState({
    offset: false,
    redraw: false,
    extraPayments: false
  });
  
  const [lender2Name, setLender2Name] = useState('');
  const [lender2Rate, setLender2Rate] = useState('');
  const [lender2Type, setLender2Type] = useState('Variable');
  const [lender2Repayment, setLender2Repayment] = useState('Principal & Interest');
  const [lender2Features, setLender2Features] = useState({
    offset: false,
    redraw: false,
    extraPayments: false
  });
  
  const [lender3Name, setLender3Name] = useState('');
  const [lender3Rate, setLender3Rate] = useState('');
  const [lender3Type, setLender3Type] = useState('Variable');
  const [lender3Repayment, setLender3Repayment] = useState('Principal & Interest');
  const [lender3Features, setLender3Features] = useState({
    offset: false,
    redraw: false,
    extraPayments: false
  });
  
  const [recommendedLender, setRecommendedLender] = useState(1); // 1, 2, or 3
  
  // Debt Consolidation (for refinance)
  const [debts, setDebts] = useState([]);
  
  // Funds Required
  const [purchasePrice, setPurchasePrice] = useState('');
  const [showStampDuty, setShowStampDuty] = useState(false);
  const [stampDuty, setStampDuty] = useState('');
  const [showRegistration, setShowRegistration] = useState(false);
  const [registration, setRegistration] = useState('');
  const [legalFees, setLegalFees] = useState('1500');
  const [lenderFees, setLenderFees] = useState('500');
  const [showLMI, setShowLMI] = useState(false);
  const [lmi, setLmi] = useState('');
  const [otherFees, setOtherFees] = useState([]);
  
  // Funds Available
  const [loanAmount, setLoanAmount] = useState('');
  const [showDeposit, setShowDeposit] = useState(false);
  const [deposit, setDeposit] = useState('');
  const [showGift, setShowGift] = useState(false);
  const [gift, setGift] = useState('');
  const [showFHOG, setShowFHOG] = useState(false);
  const [fhog, setFhog] = useState('');
  const [showOtherContributions, setShowOtherContributions] = useState(false);
  const [otherContributions, setOtherContributions] = useState('');
  
  const [showReport, setShowReport] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false); // collapsed by default; AI is the primary entry
  const [message, setMessage] = useState({ type: '', text: '' });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const parseNumber = (value) => {
    if (!value) return 0;
    const cleaned = value.toString().replace(/[$,]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const formatCurrency = (value) => {
    const num = parseNumber(value);
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const calculateTotalRequired = () => {
    let total = 0;
    
    // For purchase, include purchase price
    if (loanPurpose === 'purchase') {
      total = parseNumber(purchasePrice);
    }
    
    // For refinance, include current loan balance (what needs to be paid off)
    if (loanPurpose === 'refinance') {
      total = parseNumber(currentLoanBalance);
      // Add debts being consolidated
      debts.forEach(debt => total += parseNumber(debt.amount));
    }
    
    // Add all costs for both purchase and refinance
    if (showStampDuty) total += parseNumber(stampDuty);
    if (showRegistration) total += parseNumber(registration);
    total += parseNumber(legalFees);
    total += parseNumber(lenderFees);
    if (showLMI) total += parseNumber(lmi);
    otherFees.forEach(fee => total += parseNumber(fee.amount));
    
    return total;
  };

  const calculateTotalAvailable = () => {
    let total = parseNumber(loanAmount);
    if (showDeposit) total += parseNumber(deposit);
    if (showGift) total += parseNumber(gift);
    if (showFHOG) total += parseNumber(fhog);
    if (showOtherContributions) total += parseNumber(otherContributions);
    return total;
  };

  const totalRequired = calculateTotalRequired();
  const totalAvailable = calculateTotalAvailable();
  const difference = totalAvailable - totalRequired;
  const totalDebts = debts.reduce((sum, debt) => sum + parseNumber(debt.amount), 0);

  const addOtherFee = () => {
    setOtherFees([...otherFees, { name: '', amount: '' }]);
  };

  const removeOtherFee = (index) => {
    setOtherFees(otherFees.filter((_, i) => i !== index));
  };

  const updateOtherFee = (index, field, value) => {
    const updated = [...otherFees];
    updated[index][field] = value;
    setOtherFees(updated);
  };

  const addDebt = () => {
    setDebts([...debts, { type: 'Credit Card', name: '', amount: '' }]);
  };

  const removeDebt = (index) => {
    setDebts(debts.filter((_, i) => i !== index));
  };

  const updateDebt = (index, field, value) => {
    const updated = [...debts];
    updated[index][field] = value;
    setDebts(updated);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  // Calculate total costs for refinance
  const calculateTotalCosts = () => {
    let total = 0;
    if (showStampDuty) total += parseNumber(stampDuty);
    if (showRegistration) total += parseNumber(registration);
    total += parseNumber(legalFees);
    total += parseNumber(lenderFees);
    if (showLMI) total += parseNumber(lmi);
    otherFees.forEach(fee => total += parseNumber(fee.amount));
    return total;
  };

  // Auto-calculate loan amount for refinance
  React.useEffect(() => {
    if (loanPurpose === 'refinance') {
      // Loan Amount = Current Balance + Total Debts + Total Costs
      const totalCosts = calculateTotalCosts();
      const calculatedLoanAmount = parseNumber(currentLoanBalance) + totalDebts + totalCosts;
      
      if (calculatedLoanAmount > 0 && (parseNumber(currentLoanBalance) > 0 || totalDebts > 0)) {
        setLoanAmount(calculatedLoanAmount.toString());
      }
    }
  }, [loanPurpose, currentLoanBalance, totalDebts, stampDuty, registration, legalFees, lenderFees, lmi, otherFees, showStampDuty, showRegistration, showLMI]);

  // Auto-calculate new balance for refinance (should equal loan amount for refinance)
  React.useEffect(() => {
    if (loanPurpose === 'refinance') {
      // For refinance, new balance = loan amount (which includes everything)
      if (parseNumber(loanAmount) > 0) {
        setNewLoanBalance(loanAmount);
      }
    }
  }, [loanPurpose, loanAmount]);

  const generateReport = () => {
    // Validation based on loan purpose
    if (loanPurpose === 'purchase') {
      if (!purchasePrice || !loanAmount) {
        showMessage('error', 'Please fill in Purchase Price and Loan Amount');
        return;
      }
    } else {
      // For refinance
      if (!purchasePrice || !loanAmount) {
        showMessage('error', 'Please fill in Property Value and Loan Amount');
        return;
      }
    }
    
    if (!lender1Name || !lender2Name || !lender3Name) {
      showMessage('error', 'Please fill in all three lender options');
      return;
    }
    setShowReport(true);
    showMessage('success', 'Report generated successfully!');
  };

  const navigate = useNavigate();
  const { token: authToken } = useAuth();
  const [sending, setSending] = useState(false);
  const [shareModal, setShareModal] = useState(null); // { meetingId, proposalId, clientLink, revisitLink }

  /**
   * Build proposal payload from the form state.
   * Same shape used by `BrokerProposalSlides.js` (Purchase + Refinance decks).
   */
  const buildProposalPayload = () => ({
    loanPurpose,
    currentLoanBalance: parseNumber(currentLoanBalance),
    newLoanBalance: parseNumber(newLoanBalance),
    lenders: [
      { name: lender1Name, rate: parseNumber(lender1Rate), type: lender1Type, repayment: lender1Repayment, features: lender1Features, recommended: recommendedLender === 1 },
      { name: lender2Name, rate: parseNumber(lender2Rate), type: lender2Type, repayment: lender2Repayment, features: lender2Features, recommended: recommendedLender === 2 },
      { name: lender3Name, rate: parseNumber(lender3Rate), type: lender3Type, repayment: lender3Repayment, features: lender3Features, recommended: recommendedLender === 3 },
    ],
    debts: loanPurpose === 'refinance' ? debts.map((d) => ({ type: d.type, name: d.name, amount: parseNumber(d.amount) })) : [],
    fundsRequired: {
      purchasePrice: parseNumber(purchasePrice),
      stampDuty: showStampDuty ? parseNumber(stampDuty) : 0,
      registration: showRegistration ? parseNumber(registration) : 0,
      legalFees: parseNumber(legalFees),
      lenderFees: parseNumber(lenderFees),
      lmi: showLMI ? parseNumber(lmi) : 0,
      otherFees: otherFees.map((f) => ({ name: f.name, amount: parseNumber(f.amount) })),
    },
    fundsAvailable: {
      loanAmount: parseNumber(loanAmount),
      deposit: showDeposit ? parseNumber(deposit) : 0,
      gift: showGift ? parseNumber(gift) : 0,
      fhog: showFHOG ? parseNumber(fhog) : 0,
      otherContributions: showOtherContributions ? parseNumber(otherContributions) : 0,
    },
    totals: {
      totalRequired,
      totalAvailable,
      difference,
    },
  });

  /**
   * Send Live Proposal — primary CTA replacing the old PDF download.
   * Mints a meeting + proposal in two API calls, returns a secure shareable
   * client link that runs the live broker-driven walkthrough.
   */
  const sendLiveProposal = async () => {
    if (!authToken) {
      toast.error('You must be signed in to send a live proposal');
      return;
    }
    if (!clientName?.trim()) {
      showMessage('error', 'Please enter the client name before sending the live proposal.');
      return;
    }
    setSending(true);
    try {
      // 1. Create a meeting linked to this client.
      const proposalType = loanPurpose === 'refinance' ? 'refinance' : 'purchase';
      const proposalLabel = proposalType === 'refinance' ? 'Refinance' : 'Purchase';
      const mRes = await fetch(`${backendUrl}/api/meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          title: `${proposalLabel} Proposal — ${clientName}`,
          client_name: clientName,
        }),
      });
      if (!mRes.ok) throw new Error('Could not create meeting');
      const meeting = await mRes.json();

      // 2. Create the proposal linked to that meeting.
      const pRes = await fetch(`${backendUrl}/api/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          type: proposalType,
          title: `${proposalLabel} Proposal — ${clientName}`,
          client_name: clientName,
          meeting_id: meeting.id,
          payload: buildProposalPayload(),
        }),
      });
      if (!pRes.ok) throw new Error('Could not create proposal');
      const proposal = await pRes.json();

      const clientLink = `${window.location.origin}/join/${meeting.room_id}?t=${meeting.guest_token}`;
      const revisitLink = `${window.location.origin}/p/${proposal.revisit_token}`;
      setShareModal({ meetingId: meeting.id, proposalId: proposal.id, clientLink, revisitLink });
      showMessage('success', 'Live proposal ready! Send the link below to your client.');
    } catch (e) {
      console.error('Live proposal failed:', e);
      showMessage('error', e.message || 'Could not create live proposal');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async (link, label = 'Link') => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`${label} copied to clipboard`);
    } catch (e) {
      toast.info(link);
    }
  };

  const requiredBarWidth = totalRequired > 0 ? (totalRequired / Math.max(totalRequired, totalAvailable)) * 100 : 0;
  const availableBarWidth = totalAvailable > 0 ? (totalAvailable / Math.max(totalRequired, totalAvailable)) * 100 : 0;

  // ──────────────── AI auto-fill → state ────────────────
  // Maps the strict JSON returned by /api/breakdown/extract onto the
  // hooks above. Only sets fields the model was actually confident about
  // (null / undefined are skipped) so the broker's existing inputs are
  // preserved if they ran the AI mid-edit.
  const applyExtracted = (e) => {
    if (!e || typeof e !== 'object') return;
    const setIfStr = (setter, v) => { if (typeof v === 'string' && v.trim()) setter(v.trim()); };
    const setIfNum = (setter, v) => {
      if (typeof v === 'number' && !Number.isNaN(v)) setter(String(v));
    };
    setIfStr(setClientName, e.client_name);
    setIfStr(setBrokerName, e.broker_name);
    if (e.loan_purpose === 'purchase' || e.loan_purpose === 'refinance') {
      setLoanPurpose(e.loan_purpose);
    }
    // Funds required
    setIfNum(setPurchasePrice, e.purchase_price);
    setIfNum(setCurrentLoanBalance, e.current_loan_balance);
    setIfNum(setLoanAmount, e.loan_amount);
    if (typeof e.stamp_duty === 'number') { setStampDuty(String(e.stamp_duty)); setShowStampDuty(true); }
    if (typeof e.registration === 'number') { setRegistration(String(e.registration)); setShowRegistration(true); }
    setIfNum(setLegalFees, e.legal_fees);
    setIfNum(setLenderFees, e.lender_fees);
    if (typeof e.lmi === 'number' && e.lmi > 0) { setLmi(String(e.lmi)); setShowLMI(true); }
    if (typeof e.deposit === 'number' && e.deposit > 0) { setDeposit(String(e.deposit)); setShowDeposit(true); }
    if (typeof e.gift === 'number' && e.gift > 0) { setGift(String(e.gift)); setShowGift(true); }
    if (typeof e.fhog === 'number' && e.fhog > 0) { setFhog(String(e.fhog)); setShowFHOG(true); }
    if (typeof e.other_contributions === 'number' && e.other_contributions > 0) {
      setOtherContributions(String(e.other_contributions));
      setShowOtherContributions(true);
    }
    // Lender options — fill up to 3 distinct lenders
    if (Array.isArray(e.lenders)) {
      const setters = [
        [setLender1Name, setLender1Rate, setLender1Type, setLender1Repayment, setLender1Features],
        [setLender2Name, setLender2Rate, setLender2Type, setLender2Repayment, setLender2Features],
        [setLender3Name, setLender3Rate, setLender3Type, setLender3Repayment, setLender3Features],
      ];
      e.lenders.slice(0, 3).forEach((l, idx) => {
        if (!l) return;
        const [sn, sr, st, sp, sf] = setters[idx];
        if (l.name) sn(l.name);
        if (typeof l.rate === 'number') sr(String(l.rate));
        if (l.type === 'Variable' || l.type === 'Fixed') st(l.type);
        if (l.repayment === 'Principal & Interest' || l.repayment === 'Interest Only') sp(l.repayment);
        if (l.features && typeof l.features === 'object') {
          sf({
            offset: !!l.features.offset,
            redraw: !!l.features.redraw,
            extraPayments: !!l.features.extra_payments,
          });
        }
      });
    }
    if ([1, 2, 3].includes(e.recommended_lender_index)) {
      setRecommendedLender(e.recommended_lender_index);
    }
    // Debt consolidation (refinance flow)
    if (Array.isArray(e.debts) && e.debts.length) {
      setDebts(
        e.debts
          .filter((d) => d && (d.amount || d.name))
          .map((d) => ({
            type: d.type || 'Credit Card',
            name: d.name || '',
            amount: typeof d.amount === 'number' ? String(d.amount) : (d.amount || ''),
          }))
      );
    }
    // ▸ Render the report directly — broker explicitly asked for "produce
    //   the report based on the info provided", skipping the manual review
    //   step. The Report Output card on the right populates immediately.
    //   Defer to the next tick so all state setters have committed first.
    setTimeout(() => {
      setShowReport(true);
      const reportEl = document.querySelector('[data-testid="report-output-card"]');
      if (reportEl) {
        reportEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 max-w-7xl">
        {/* Hero Section */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="w-10 h-10 text-blue-600" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-teal-500 to-green-600 text-transparent bg-clip-text">
              Break Down Report
            </h1>
          </div>
          <p className="text-lg text-slate-700 max-w-3xl mx-auto">
            Generate professional breakdown reports comparing funds required versus funds available
          </p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`max-w-4xl mx-auto mb-6 flex items-center gap-2 p-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        <div className={`grid gap-8 ${showManualForm ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>
          {/* Left Column - Input Forms */}
          <div className="space-y-6">
            {/* AI is the primary entry surface — broker drops screenshots / notes and
                a polished multi-page PDF proposal is generated inline. The manual
                form below is preserved for ad-hoc tweaks but collapsed by default. */}
            <BreakdownProposalGenerator
              onSendLive={() => {
                // Reuse the existing live-proposal flow. We keep the manual form
                // populated in the background so this still works.
                toast.info('Opening Live Proposal — fill in any missing fields below if needed.');
                setShowManualForm(true);
                setTimeout(() => {
                  document.querySelector('[data-testid="report-output-card"]')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            />

            {/* Alternative entry: broker uploads a PDF made on another platform
                and presents it page-by-page in the same live meeting room. */}
            <UploadPdfProposalCard />

            <button
              type="button"
              onClick={() => setShowManualForm((v) => !v)}
              className="w-full text-left flex items-center justify-between px-4 py-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition"
              data-testid="manual-form-toggle"
            >
              <span className="text-sm font-semibold text-slate-700">
                {showManualForm ? '− Hide manual entry' : '+ Or fill the form manually'}
              </span>
              <span className="text-xs text-slate-500">{showManualForm ? 'Hide' : 'Show'}</span>
            </button>

            {showManualForm && (<>
            {/* Client & Broker Info */}
            <Card className="border-2 border-blue-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-teal-50">
                <CardTitle className="text-xl font-bold text-slate-800">Client & Broker Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Client Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="John & Mary Smith"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="border-slate-300 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Broker Name (Optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={brokerName}
                    onChange={(e) => setBrokerName(e.target.value)}
                    className="border-slate-300 focus:ring-blue-500"
                  />
                </div>
                
                {/* Loan Purpose Toggle */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Loan Purpose <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="purchase"
                        checked={loanPurpose === 'purchase'}
                        onChange={(e) => setLoanPurpose(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-slate-700">Purchase</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="refinance"
                        checked={loanPurpose === 'refinance'}
                        onChange={(e) => setLoanPurpose(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-slate-700">Refinance with Debt Consolidation</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lender Options */}
            <Card className="border-2 border-purple-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardTitle className="text-xl font-bold text-slate-800">Lender Options (3 Required)</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Lender 1 */}
                <div className={`p-4 rounded-lg border-2 ${recommendedLender === 1 ? 'border-green-400 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-slate-800">Option 1</h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={recommendedLender === 1}
                        onChange={() => setRecommendedLender(1)}
                        className="w-4 h-4"
                      />
                      <Star className={`w-5 h-5 ${recommendedLender === 1 ? 'fill-green-600 text-green-600' : 'text-slate-400'}`} />
                      <span className="text-sm font-semibold text-slate-700">Recommended</span>
                    </label>
                  </div>
                  <div className="space-y-3">
                    <Input
                      placeholder="Lender Name *"
                      value={lender1Name}
                      onChange={(e) => setLender1Name(e.target.value)}
                      className="border-slate-300"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Interest Rate (e.g., 6.5)"
                        value={lender1Rate}
                        onChange={(e) => setLender1Rate(e.target.value)}
                        className="border-slate-300"
                      />
                      <select
                        value={lender1Type}
                        onChange={(e) => setLender1Type(e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Variable">Variable</option>
                        <option value="Fixed">Fixed</option>
                      </select>
                    </div>
                    <select
                      value={lender1Repayment}
                      onChange={(e) => setLender1Repayment(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Principal & Interest">Principal & Interest</option>
                      <option value="Interest Only">Interest Only</option>
                    </select>
                    <div className="flex gap-4 text-sm">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender1Features.offset}
                          onChange={(e) => setLender1Features({...lender1Features, offset: e.target.checked})}
                        />
                        <span>Offset</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender1Features.redraw}
                          onChange={(e) => setLender1Features({...lender1Features, redraw: e.target.checked})}
                        />
                        <span>Redraw</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender1Features.extraPayments}
                          onChange={(e) => setLender1Features({...lender1Features, extraPayments: e.target.checked})}
                        />
                        <span>Extra Payments</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Lender 2 */}
                <div className={`p-4 rounded-lg border-2 ${recommendedLender === 2 ? 'border-green-400 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-slate-800">Option 2</h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={recommendedLender === 2}
                        onChange={() => setRecommendedLender(2)}
                        className="w-4 h-4"
                      />
                      <Star className={`w-5 h-5 ${recommendedLender === 2 ? 'fill-green-600 text-green-600' : 'text-slate-400'}`} />
                      <span className="text-sm font-semibold text-slate-700">Recommended</span>
                    </label>
                  </div>
                  <div className="space-y-3">
                    <Input
                      placeholder="Lender Name *"
                      value={lender2Name}
                      onChange={(e) => setLender2Name(e.target.value)}
                      className="border-slate-300"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Interest Rate (e.g., 6.8)"
                        value={lender2Rate}
                        onChange={(e) => setLender2Rate(e.target.value)}
                        className="border-slate-300"
                      />
                      <select
                        value={lender2Type}
                        onChange={(e) => setLender2Type(e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Variable">Variable</option>
                        <option value="Fixed">Fixed</option>
                      </select>
                    </div>
                    <select
                      value={lender2Repayment}
                      onChange={(e) => setLender2Repayment(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Principal & Interest">Principal & Interest</option>
                      <option value="Interest Only">Interest Only</option>
                    </select>
                    <div className="flex gap-4 text-sm">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender2Features.offset}
                          onChange={(e) => setLender2Features({...lender2Features, offset: e.target.checked})}
                        />
                        <span>Offset</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender2Features.redraw}
                          onChange={(e) => setLender2Features({...lender2Features, redraw: e.target.checked})}
                        />
                        <span>Redraw</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender2Features.extraPayments}
                          onChange={(e) => setLender2Features({...lender2Features, extraPayments: e.target.checked})}
                        />
                        <span>Extra Payments</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Lender 3 */}
                <div className={`p-4 rounded-lg border-2 ${recommendedLender === 3 ? 'border-green-400 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-slate-800">Option 3</h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={recommendedLender === 3}
                        onChange={() => setRecommendedLender(3)}
                        className="w-4 h-4"
                      />
                      <Star className={`w-5 h-5 ${recommendedLender === 3 ? 'fill-green-600 text-green-600' : 'text-slate-400'}`} />
                      <span className="text-sm font-semibold text-slate-700">Recommended</span>
                    </label>
                  </div>
                  <div className="space-y-3">
                    <Input
                      placeholder="Lender Name *"
                      value={lender3Name}
                      onChange={(e) => setLender3Name(e.target.value)}
                      className="border-slate-300"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Interest Rate (e.g., 6.9)"
                        value={lender3Rate}
                        onChange={(e) => setLender3Rate(e.target.value)}
                        className="border-slate-300"
                      />
                      <select
                        value={lender3Type}
                        onChange={(e) => setLender3Type(e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Variable">Variable</option>
                        <option value="Fixed">Fixed</option>
                      </select>
                    </div>
                    <select
                      value={lender3Repayment}
                      onChange={(e) => setLender3Repayment(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Principal & Interest">Principal & Interest</option>
                      <option value="Interest Only">Interest Only</option>
                    </select>
                    <div className="flex gap-4 text-sm">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender3Features.offset}
                          onChange={(e) => setLender3Features({...lender3Features, offset: e.target.checked})}
                        />
                        <span>Offset</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender3Features.redraw}
                          onChange={(e) => setLender3Features({...lender3Features, redraw: e.target.checked})}
                        />
                        <span>Redraw</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={lender3Features.extraPayments}
                          onChange={(e) => setLender3Features({...lender3Features, extraPayments: e.target.checked})}
                        />
                        <span>Extra Payments</span>
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Debt Consolidation (Only for Refinance) */}
            {loanPurpose === 'refinance' && (
              <Card className="border-2 border-orange-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50">
                  <CardTitle className="text-xl font-bold text-slate-800">Refinance Details</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  {/* Current and New Loan Balances */}
                  <div className="grid grid-cols-2 gap-4 pb-4 border-b border-orange-200">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Current Home Loan Balance
                      </label>
                      <Input
                        type="text"
                        placeholder="$450,000"
                        value={currentLoanBalance}
                        onChange={(e) => setCurrentLoanBalance(e.target.value)}
                        className="border-slate-300 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        New Balance After Everything <span className="text-xs text-slate-500">(Auto-calculated)</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="Auto-calculated"
                        value={formatCurrency(newLoanBalance)}
                        readOnly
                        className="border-slate-300 bg-slate-50 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-slate-800 pt-2">Debts Being Consolidated</h3>
                  {debts.map((debt, index) => (
                    <div key={index} className="flex gap-2">
                      <select
                        value={debt.type}
                        onChange={(e) => updateDebt(index, 'type', e.target.value)}
                        className="w-40 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="Credit Card">Credit Card</option>
                        <option value="Personal Loan">Personal Loan</option>
                        <option value="Car Loan">Car Loan</option>
                        <option value="Other">Other Debt</option>
                      </select>
                      <Input
                        type="text"
                        placeholder="Institution name"
                        value={debt.name}
                        onChange={(e) => updateDebt(index, 'name', e.target.value)}
                        className="flex-1 border-slate-300 focus:ring-orange-500"
                      />
                      <Input
                        type="text"
                        placeholder="$0"
                        value={debt.amount}
                        onChange={(e) => updateDebt(index, 'amount', e.target.value)}
                        className="w-32 border-slate-300 focus:ring-orange-500"
                      />
                      <Button
                        onClick={() => removeDebt(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={addDebt}
                    variant="outline"
                    size="sm"
                    className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Debt
                  </Button>
                  
                  {debts.length > 0 && (
                    <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-orange-900">Total Debts Consolidated</span>
                        <span className="text-2xl font-bold text-orange-700">{formatCurrency(totalDebts)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Funds Required - Same as before but modified */}
            <Card className="border-2 border-blue-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-teal-50">
                <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  {loanPurpose === 'purchase' ? 'Funds Required' : 'Additional Costs'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    {loanPurpose === 'purchase' ? 'Purchase Price' : 'Property Value'} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="$750,000"
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                    className="border-slate-300 focus:ring-blue-500"
                  />
                </div>

                {/* Stamp Duty */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">Stamp Duty</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showStampDuty}
                        onChange={(e) => setShowStampDuty(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  {showStampDuty && (
                    <Input
                      type="text"
                      placeholder="$35,000"
                      value={stampDuty}
                      onChange={(e) => setStampDuty(e.target.value)}
                      className="border-slate-300 focus:ring-blue-500"
                    />
                  )}
                </div>

                {/* Registration of Mortgage */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">Registration of Mortgage</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showRegistration}
                        onChange={(e) => setShowRegistration(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  {showRegistration && (
                    <Input
                      type="text"
                      placeholder="$1,200"
                      value={registration}
                      onChange={(e) => setRegistration(e.target.value)}
                      className="border-slate-300 focus:ring-blue-500"
                    />
                  )}
                </div>

                {/* Legal Fees */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Legal / Solicitor Fees
                  </label>
                  <Input
                    type="text"
                    value={legalFees}
                    onChange={(e) => setLegalFees(e.target.value)}
                    className="border-slate-300 focus:ring-blue-500"
                  />
                </div>

                {/* Lender Fees */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Lender Fees
                  </label>
                  <Input
                    type="text"
                    value={lenderFees}
                    onChange={(e) => setLenderFees(e.target.value)}
                    className="border-slate-300 focus:ring-blue-500"
                  />
                </div>

                {/* LMI */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">LMI (Lenders Mortgage Insurance)</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showLMI}
                        onChange={(e) => setShowLMI(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  {showLMI && (
                    <Input
                      type="text"
                      placeholder="$15,000"
                      value={lmi}
                      onChange={(e) => setLmi(e.target.value)}
                      className="border-slate-300 focus:ring-blue-500"
                    />
                  )}
                </div>

                {/* Other Fees */}
                <div className="border-t border-slate-200 pt-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Other Fees</label>
                  {otherFees.map((fee, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        type="text"
                        placeholder="Fee name"
                        value={fee.name}
                        onChange={(e) => updateOtherFee(index, 'name', e.target.value)}
                        className="flex-1 border-slate-300 focus:ring-blue-500"
                      />
                      <Input
                        type="text"
                        placeholder="$0"
                        value={fee.amount}
                        onChange={(e) => updateOtherFee(index, 'amount', e.target.value)}
                        className="w-32 border-slate-300 focus:ring-blue-500"
                      />
                      <Button
                        onClick={() => removeOtherFee(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={addOtherFee}
                    variant="outline"
                    size="sm"
                    className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Fee
                  </Button>
                </div>

                {/* Total Required */}
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-blue-900">Total Funds Required</span>
                    <span className="text-2xl font-bold text-blue-700">{formatCurrency(totalRequired)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Funds Available */}
            <Card className="border-2 border-green-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-green-50 to-teal-50">
                <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Funds Available
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Loan Amount <span className="text-red-500">*</span>
                    {loanPurpose === 'refinance' && <span className="text-xs text-slate-500 ml-1">(Auto-calculated)</span>}
                  </label>
                  <Input
                    type="text"
                    placeholder={loanPurpose === 'refinance' ? 'Auto-calculated' : '$600,000'}
                    value={loanPurpose === 'refinance' ? formatCurrency(loanAmount) : loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    readOnly={loanPurpose === 'refinance'}
                    className={`border-slate-300 focus:ring-green-500 ${loanPurpose === 'refinance' ? 'bg-slate-50 cursor-not-allowed' : ''}`}
                  />
                </div>

                {/* Deposit, Gift, FHOG only for Purchase */}
                {loanPurpose === 'purchase' && (
                  <>
                    {/* Paid Deposit */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">Paid Deposit</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showDeposit}
                        onChange={(e) => setShowDeposit(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                  {showDeposit && (
                    <Input
                      type="text"
                      placeholder="$50,000"
                      value={deposit}
                      onChange={(e) => setDeposit(e.target.value)}
                      className="border-slate-300 focus:ring-green-500"
                    />
                  )}
                </div>

                {/* Gift */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">Gift</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showGift}
                        onChange={(e) => setShowGift(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                  {showGift && (
                    <Input
                      type="text"
                      placeholder="$20,000"
                      value={gift}
                      onChange={(e) => setGift(e.target.value)}
                      className="border-slate-300 focus:ring-green-500"
                    />
                  )}
                </div>

                {/* FHOG */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">FHOG (First Home Owner Grant)</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showFHOG}
                        onChange={(e) => setShowFHOG(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                  {showFHOG && (
                    <Input
                      type="text"
                      placeholder="$10,000"
                      value={fhog}
                      onChange={(e) => setFhog(e.target.value)}
                      className="border-slate-300 focus:ring-green-500"
                    />
                  )}
                </div>

                {/* Other Contributions */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">Other Contributions</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showOtherContributions}
                        onChange={(e) => setShowOtherContributions(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                  {showOtherContributions && (
                    <Input
                      type="text"
                      placeholder="$5,000"
                      value={otherContributions}
                      onChange={(e) => setOtherContributions(e.target.value)}
                      className="border-slate-300 focus:ring-green-500"
                    />
                  )}
                </div>
                  </>
                )}

                {/* Total Available */}
                <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-green-900">Total Funds Available</span>
                    <span className="text-2xl font-bold text-green-700">{formatCurrency(totalAvailable)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Generate Report Button */}
            <Button
              onClick={generateReport}
              className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-bold py-6 text-lg"
            >
              <FileText className="mr-2 h-5 w-5" />
              Generate Break Down Report
            </Button>
            </>)}
          </div>

          {/* Right Column - Manual report (only when broker uses the manual form) */}
          {showManualForm && (
          <div data-testid="report-output-card">
            <Card className="border-2 border-blue-200 shadow-lg sticky top-4">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50">
                <CardTitle className="text-2xl font-bold text-slate-800">Report Output</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {!showReport ? (
                  <div className="text-center py-16">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 text-lg">
                      Drop screenshots or paste notes above &mdash; the report appears here
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {/* Report Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-teal-600 text-white p-6 rounded-lg">
                      <h2 className="text-2xl font-bold mb-2">Loan Proposal Summary</h2>
                      <p className="text-blue-100">For: {clientName}</p>
                      {brokerName && <p className="text-blue-100 text-sm">Prepared by: {brokerName}</p>}
                    </div>

                    {/* Loan Details Summary */}
                    <div className="bg-white rounded-lg p-5 border-2 border-blue-200 shadow-sm">
                      <h3 className="text-lg font-bold text-blue-800 mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Key Loan Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-600 font-semibold">Loan Purpose:</span>
                          <span className="text-slate-900 font-bold capitalize">{loanPurpose}</span>
                        </div>
                        {loanPurpose === 'purchase' && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-600 font-semibold">Purchase Price:</span>
                            <span className="text-slate-900 font-bold">{formatCurrency(purchasePrice)}</span>
                          </div>
                        )}
                        {loanPurpose === 'refinance' && (
                          <>
                            <div className="flex justify-between py-1 border-b border-slate-100">
                              <span className="text-slate-600 font-semibold">Property Value:</span>
                              <span className="text-slate-900 font-bold">{formatCurrency(purchasePrice)}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-100">
                              <span className="text-slate-600 font-semibold">Current Loan Balance:</span>
                              <span className="text-slate-900 font-bold">{formatCurrency(currentLoanBalance)}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-600 font-semibold">Loan Amount:</span>
                          <span className="text-blue-900 font-bold text-lg">{formatCurrency(loanAmount)}</span>
                        </div>
                        {loanPurpose === 'refinance' && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-600 font-semibold">New Balance After Everything:</span>
                            <span className="text-blue-900 font-bold text-lg">{formatCurrency(newLoanBalance)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Lender Comparison */}
                    <div className="bg-white rounded-lg p-5 border-2 border-purple-200 shadow-sm">
                      <h3 className="text-lg font-bold text-purple-800 mb-3">Lender Options Comparison</h3>
                      <div className="grid grid-cols-3 gap-3">
                        <div className={`p-3 rounded-lg border-2 ${recommendedLender === 1 ? 'border-green-500 bg-green-50' : 'border-slate-300'}`}>
                          {recommendedLender === 1 && (
                            <div className="flex items-center gap-1 mb-2 text-green-700 font-bold">
                              <Star className="w-3 h-3 fill-green-700" />
                              <span className="text-xs">TOP PICK</span>
                            </div>
                          )}
                          <div className="text-xs font-bold text-slate-800 mb-1">{lender1Name}</div>
                          <div className="text-xs text-slate-600 space-y-0.5">
                            <div>{lender1Rate}% {lender1Type}</div>
                            <div className="text-xs">{lender1Repayment}</div>
                          </div>
                        </div>
                        <div className={`p-3 rounded-lg border-2 ${recommendedLender === 2 ? 'border-green-500 bg-green-50' : 'border-slate-300'}`}>
                          {recommendedLender === 2 && (
                            <div className="flex items-center gap-1 mb-2 text-green-700 font-bold">
                              <Star className="w-3 h-3 fill-green-700" />
                              <span className="text-xs">TOP PICK</span>
                            </div>
                          )}
                          <div className="text-xs font-bold text-slate-800 mb-1">{lender2Name}</div>
                          <div className="text-xs text-slate-600 space-y-0.5">
                            <div>{lender2Rate}% {lender2Type}</div>
                            <div className="text-xs">{lender2Repayment}</div>
                          </div>
                        </div>
                        <div className={`p-3 rounded-lg border-2 ${recommendedLender === 3 ? 'border-green-500 bg-green-50' : 'border-slate-300'}`}>
                          {recommendedLender === 3 && (
                            <div className="flex items-center gap-1 mb-2 text-green-700 font-bold">
                              <Star className="w-3 h-3 fill-green-700" />
                              <span className="text-xs">TOP PICK</span>
                            </div>
                          )}
                          <div className="text-xs font-bold text-slate-800 mb-1">{lender3Name}</div>
                          <div className="text-xs text-slate-600 space-y-0.5">
                            <div>{lender3Rate}% {lender3Type}</div>
                            <div className="text-xs">{lender3Repayment}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Recommended lender details */}
                      <div className="mt-4 bg-green-50 border-l-4 border-green-500 p-3 rounded">
                        <p className="text-sm font-semibold text-green-900">
                          ★ Recommended: {recommendedLender === 1 ? lender1Name : recommendedLender === 2 ? lender2Name : lender3Name}
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          Features: {recommendedLender === 1 ? 
                            [lender1Features.offset && 'Offset', lender1Features.redraw && 'Redraw', lender1Features.extraPayments && 'Extra Payments'].filter(Boolean).join(', ') :
                            recommendedLender === 2 ?
                            [lender2Features.offset && 'Offset', lender2Features.redraw && 'Redraw', lender2Features.extraPayments && 'Extra Payments'].filter(Boolean).join(', ') :
                            [lender3Features.offset && 'Offset', lender3Features.redraw && 'Redraw', lender3Features.extraPayments && 'Extra Payments'].filter(Boolean).join(', ')
                          }
                        </p>
                      </div>
                    </div>

                    {/* Refinance Summary - Debts */}
                    {loanPurpose === 'refinance' && debts.length > 0 && (
                      <div className="bg-white rounded-lg p-5 border-2 border-orange-200 shadow-sm">
                        <h3 className="text-lg font-bold text-orange-800 mb-3">Debts Being Consolidated</h3>
                        <div className="space-y-2">
                          {debts.map((debt, idx) => (
                            <div key={idx} className="flex justify-between py-2 border-b border-orange-100">
                              <div>
                                <span className="text-sm font-semibold text-slate-700">{debt.type}</span>
                                {debt.name && <span className="text-xs text-slate-500 ml-2">({debt.name})</span>}
                              </div>
                              <span className="text-sm font-bold text-orange-900">{formatCurrency(debt.amount)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between py-2 bg-orange-50 rounded px-3 mt-2">
                            <span className="text-sm font-bold text-orange-900">Total Debts Consolidated:</span>
                            <span className="text-lg font-bold text-orange-700">{formatCurrency(totalDebts)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Costs Breakdown */}
                    <div className="bg-white rounded-lg p-5 border-2 border-blue-200 shadow-sm">
                      <h3 className="text-lg font-bold text-blue-800 mb-3">
                        {loanPurpose === 'purchase' ? 'Estimated Upfront Costs' : 'Additional Costs'}
                      </h3>
                      <div className="space-y-2 text-sm">
                        {loanPurpose === 'purchase' ? (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-600">Purchase Price</span>
                            <span className="font-semibold text-slate-900">{formatCurrency(purchasePrice)}</span>
                          </div>
                        ) : (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-600">Property Value</span>
                            <span className="font-semibold text-slate-900">{formatCurrency(purchasePrice)}</span>
                          </div>
                        )}
                        {showStampDuty && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-600">Stamp Duty</span>
                            <span className="font-semibold text-slate-900">{formatCurrency(stampDuty)}</span>
                          </div>
                        )}
                        {showLMI && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-600">LMI</span>
                            <span className="font-semibold text-slate-900">{formatCurrency(lmi)}</span>
                          </div>
                        )}
                        {showRegistration && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-600">Registration of Mortgage</span>
                            <span className="font-semibold text-slate-900">{formatCurrency(registration)}</span>
                          </div>
                        )}
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-600">Legal / Solicitor Fees</span>
                          <span className="font-semibold text-slate-900">{formatCurrency(legalFees)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-600">Lender Fees</span>
                          <span className="font-semibold text-slate-900">{formatCurrency(lenderFees)}</span>
                        </div>
                        {otherFees.map((fee, idx) => (
                          fee.name && (
                            <div key={idx} className="flex justify-between py-1 border-b border-slate-100">
                              <span className="text-slate-600">{fee.name}</span>
                              <span className="font-semibold text-slate-900">{formatCurrency(fee.amount)}</span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>

                    {/* Visual Comparison */}
                    <div className="bg-white rounded-lg p-5 border-2 border-slate-200 shadow-sm">
                      <h3 className="text-lg font-bold text-slate-800 mb-4">Funds Comparison</h3>
                      
                      {/* Required Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-semibold text-blue-700">Funds Required</span>
                          <span className="text-sm font-bold text-blue-900">{formatCurrency(totalRequired)}</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-8">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-8 rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${requiredBarWidth}%` }}
                          >
                            <span className="text-white text-xs font-bold">{Math.round(requiredBarWidth)}%</span>
                          </div>
                        </div>
                      </div>

                      {/* Available Bar */}
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-semibold text-green-700">Funds Available</span>
                          <span className="text-sm font-bold text-green-900">{formatCurrency(totalAvailable)}</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-8">
                          <div
                            className="bg-gradient-to-r from-green-500 to-green-600 h-8 rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${availableBarWidth}%` }}
                          >
                            <span className="text-white text-xs font-bold">{Math.round(availableBarWidth)}%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Funding Summary */}
                    <div className="bg-white rounded-lg p-5 border-2 border-slate-200 shadow-sm">
                      <h3 className="text-lg font-bold text-slate-800 mb-3">Funding Summary</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2">
                          <span className="text-sm font-semibold text-blue-700">Total Funds Required:</span>
                          <span className="text-lg font-bold text-blue-900">{formatCurrency(totalRequired)}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-sm font-semibold text-green-700">Total Funds Available:</span>
                          <span className="text-lg font-bold text-green-900">{formatCurrency(totalAvailable)}</span>
                        </div>
                        <div className={`flex justify-between py-3 px-4 rounded-lg ${
                          difference >= 0 ? 'bg-green-50' : 'bg-red-50'
                        }`}>
                          <div className="flex items-center gap-2">
                            {difference >= 0 ? (
                              <TrendingUp className="w-5 h-5 text-green-600" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-600" />
                            )}
                            <span className={`text-base font-bold ${
                              difference >= 0 ? 'text-green-900' : 'text-red-900'
                            }`}>
                              {difference >= 0 ? 'Surplus:' : 'Shortfall:'}
                            </span>
                          </div>
                          <span className={`text-xl font-bold ${
                            difference >= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {formatCurrency(Math.abs(difference))}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Next Steps */}
                    <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg p-5 border-2 border-blue-200 shadow-sm">
                      <h3 className="text-lg font-bold text-blue-900 mb-3">Next Steps</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-slate-800">Loan Submission</span>
                            <span className="text-slate-600"> – Application submitted for approval</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-slate-800">Conditional Approval</span>
                            <span className="text-slate-600"> – Expect feedback within 3-5 business days</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-slate-800">Settlement</span>
                            <span className="text-slate-600"> – Finalize and proceed to settlement</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Send Live Proposal Button */}
                    <div className="sticky bottom-0 bg-white pt-4 pb-2 border-t-2 border-emerald-200">
                      <Button
                        onClick={sendLiveProposal}
                        disabled={sending}
                        data-testid="send-live-proposal-btn"
                        className="w-full bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 hover:from-emerald-600 hover:via-emerald-700 hover:to-teal-700 text-white font-bold py-4 text-lg shadow-lg disabled:opacity-60"
                      >
                        {sending ? (
                          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating live proposal…</>
                        ) : (
                          <><Send className="w-5 h-5 mr-2" /> Send Live Proposal</>
                        )}
                      </Button>
                      <p className="text-xs text-center text-slate-500 mt-2">
                        Generates a secure client link. You'll present the proposal live over video.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}
        </div>
      </main>

      {/* Share modal — appears after sendLiveProposal succeeds */}
      <Dialog open={!!shareModal} onOpenChange={(o) => !o && setShareModal(null)}>
        <DialogContent data-testid="live-proposal-share-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-emerald-500" /> Live proposal ready
            </DialogTitle>
          </DialogHeader>
          {shareModal && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-600">
                Send this secure link to your client. They click and join the live walkthrough — no login, no app.
              </p>
              <div>
                <p className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1">Client meeting link</p>
                <div className="bg-slate-100 border border-slate-200 rounded-md px-3 py-2 text-xs break-all font-mono" data-testid="client-meeting-link">
                  {shareModal.clientLink}
                </div>
                <Button
                  onClick={() => copyLink(shareModal.clientLink, 'Client link')}
                  data-testid="copy-client-link-btn"
                  className="mt-2 bg-emerald-600 hover:bg-emerald-700 w-full"
                >
                  <Copy className="h-4 w-4 mr-2" /> Copy Client Link
                </Button>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1">Revisit link (read-only)</p>
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs break-all font-mono text-slate-600" data-testid="revisit-link">
                  {shareModal.revisitLink}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Share this if the client wants to revisit the proposal later — valid for 30 days.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyLink(shareModal.revisitLink, 'Revisit link')}
                  data-testid="copy-revisit-link-btn"
                  className="mt-2 w-full"
                >
                  <Copy className="h-3.5 w-3.5 mr-2" /> Copy Revisit Link
                </Button>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShareModal(null)}>Close</Button>
            {shareModal && (
              <Button
                data-testid="enter-room-from-share-btn"
                onClick={() => navigate(`/meeting/${shareModal.meetingId}`)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" /> Enter Meeting Room
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default BreakDownReport;
